import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { BaseLayout } from '@/components/layout/BaseLayout';
import { ThemedCard } from '@/components/ui/themed-card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  HashIcon, 
  UserIcon, 
  PawPrintIcon, 
  SettingsIcon,
  CalendarIcon,
  CreditCardIcon,
  CheckIcon,
  AlertTriangleIcon
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

type RaffleType = 'numbers' | 'names' | 'jogo-do-bicho' | 'animals-misticos' | 'cidades-europa' | 'cidades-brasil' | 'frutas' | 'cores' | 'planetas' | 'signos' | 'cartas' | 'times-futebol' | 'profissoes' | 'marcas-carros' | 'paises' | 'flores' | 'pedras-preciosas' | 'custom';

interface RaffleTypeConfig {
  id: RaffleType;
  title: string;
  description: string;
  icon: string;
  maxNumbers: number;
  isPopular?: boolean;
  examples?: string[];
}

const raffleTypes: RaffleTypeConfig[] = [
  // 3 mais populares primeiro
  {
    id: 'numbers',
    title: 'Números',
    description: 'Participantes escolhem números específicos',
    icon: '#️⃣',
    maxNumbers: 10000,
    isPopular: true,
    examples: ['01', '02', '03', '...', '100']
  },
  {
    id: 'jogo-do-bicho',
    title: 'Jogo do Bicho',
    description: 'Baseado nos 25 animais tradicionais',
    icon: '🐘',
    maxNumbers: 25,
    isPopular: true,
    examples: ['Avestruz', 'Águia', 'Burro', 'Borboleta', 'Cachorro']
  },
  {
    id: 'names',
    title: 'Nomes',
    description: 'Participantes registram seus nomes',
    icon: '👤',
    maxNumbers: 1000,
    isPopular: true,
    examples: ['João Silva', 'Maria Santos', 'Pedro Costa']
  },
  
  // Outras opções
  {
    id: 'animals-misticos',
    title: 'Animais Místicos',
    description: 'Criaturas míticas e fantásticas',
    icon: '🦄',
    maxNumbers: 50,
    examples: ['Dragão', 'Fênix', 'Unicórnio', 'Grifo', 'Kraken']
  },
  {
    id: 'cidades-europa',
    title: 'Cidades da Europa',
    description: 'Principais cidades europeias',
    icon: '🏰',
    maxNumbers: 100,
    examples: ['Paris', 'Londres', 'Roma', 'Berlim', 'Madrid']
  },
  {
    id: 'cidades-brasil',
    title: 'Cidades do Brasil',
    description: 'Capitais e cidades importantes',
    icon: '🇧🇷',
    maxNumbers: 100,
    examples: ['São Paulo', 'Rio de Janeiro', 'Salvador', 'Brasília']
  },
  {
    id: 'frutas',
    title: 'Frutas',
    description: 'Diversas frutas tropicais e tradicionais',
    icon: '🍎',
    maxNumbers: 80,
    examples: ['Maçã', 'Banana', 'Laranja', 'Manga', 'Abacaxi']
  },
  {
    id: 'cores',
    title: 'Cores',
    description: 'Paleta de cores variadas',
    icon: '🎨',
    maxNumbers: 50,
    examples: ['Vermelho', 'Azul', 'Verde', 'Amarelo', 'Roxo']
  },
  {
    id: 'planetas',
    title: 'Planetas e Estrelas',
    description: 'Corpos celestes do universo',
    icon: '🪐',
    maxNumbers: 100,
    examples: ['Terra', 'Marte', 'Júpiter', 'Saturno', 'Vênus']
  },
  {
    id: 'signos',
    title: 'Signos do Zodíaco',
    description: 'Os 12 signos astrológicos',
    icon: '♈',
    maxNumbers: 12,
    examples: ['Áries', 'Touro', 'Gêmeos', 'Câncer', 'Leão']
  },
  {
    id: 'cartas',
    title: 'Cartas de Baralho',
    description: 'Cartas tradicionais do baralho',
    icon: '🃏',
    maxNumbers: 52,
    examples: ['Ás de Copas', 'Rei de Espadas', 'Dama de Ouros']
  },
  {
    id: 'times-futebol',
    title: 'Times de Futebol',
    description: 'Principais times brasileiros',
    icon: '⚽',
    maxNumbers: 40,
    examples: ['Flamengo', 'Corinthians', 'Palmeiras', 'São Paulo']
  },
  {
    id: 'profissoes',
    title: 'Profissões',
    description: 'Diversas áreas profissionais',
    icon: '👔',
    maxNumbers: 100,
    examples: ['Médico', 'Professor', 'Engenheiro', 'Advogado']
  },
  {
    id: 'marcas-carros',
    title: 'Marcas de Carros',
    description: 'Principais montadoras mundiais',
    icon: '🚗',
    maxNumbers: 50,
    examples: ['Toyota', 'Volkswagen', 'Ford', 'Chevrolet']
  },
  {
    id: 'paises',
    title: 'Países',
    description: 'Países de todos os continentes',
    icon: '🌍',
    maxNumbers: 195,
    examples: ['Brasil', 'Estados Unidos', 'França', 'Japão']
  },
  {
    id: 'flores',
    title: 'Flores',
    description: 'Variedades de flores e plantas',
    icon: '🌸',
    maxNumbers: 60,
    examples: ['Rosa', 'Lírio', 'Tulipa', 'Orquídea', 'Girassol']
  },
  {
    id: 'pedras-preciosas',
    title: 'Pedras Preciosas',
    description: 'Gemas e minerais valiosos',
    icon: '💎',
    maxNumbers: 40,
    examples: ['Diamante', 'Rubi', 'Esmeralda', 'Safira', 'Ametista']
  },
  {
    id: 'custom',
    title: 'Personalizado',
    description: 'Configure do seu jeito',
    icon: '⚙️',
    maxNumbers: 10000,
    examples: ['Defina suas próprias opções']
  }
];

const getRaffleTypeConfig = (type: RaffleType): RaffleTypeConfig => {
  return raffleTypes.find(t => t.id === type) || raffleTypes[0];
};

const getRaffleTypeLabel = (type: RaffleType): string => {
  return getRaffleTypeConfig(type).title;
};

interface RaffleFormData {
  title: string;
  description: string;
  type: RaffleType;
  total_spots: number;
  price_per_spot: number;
  prize_description: string;
  prize_image?: File;
  draw_date: string;
  pix_key: string;
  pix_beneficiary: string;
}

const CreateRafflePage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editRaffleId = searchParams.get('edit');
  const isEditMode = !!editRaffleId;
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [createdRaffleId, setCreatedRaffleId] = useState<string | null>(null);
  const [formData, setFormData] = useState<RaffleFormData>({
    title: '',
    description: '',
    type: 'numbers',
    total_spots: 100,
    price_per_spot: 5,
    prize_description: '',
    draw_date: '',
    pix_key: '',
    pix_beneficiary: ''
  });

  const totalSteps = 5;

  // Load raffle data for editing
  useEffect(() => {
    if (isEditMode && editRaffleId && user) {
      loadRaffleForEdit(editRaffleId);
    }
  }, [isEditMode, editRaffleId, user]);

  const loadRaffleForEdit = async (raffleId: string) => {
    try {
      setLoading(true);
      const { data: raffle, error } = await supabase
        .from('raffles')
        .select('*')
        .eq('id', raffleId)
        .eq('creator_id', user?.id) // Ensure user owns this raffle
        .eq('status', 'draft') // Only allow editing draft raffles
        .single();

      if (error) throw error;
      if (!raffle) {
        navigate('/creator-dashboard');
        return;
      }

      // Convert raffle data to form format
      setFormData({
        title: raffle.title,
        description: raffle.description || '',
        type: raffle.type as RaffleType,
        total_spots: raffle.total_spots,
        price_per_spot: raffle.price_per_spot,
        prize_description: raffle.prize_description || '',
        draw_date: raffle.draw_date ? new Date(raffle.draw_date).toISOString().slice(0, 16) : '',
        pix_key: raffle.pix_key || '',
        pix_beneficiary: raffle.pix_beneficiary || ''
      });
      
      setCreatedRaffleId(raffleId);
    } catch (error) {
      console.error('Error loading raffle for edit:', error);
      navigate('/creator-dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        prize_image: file
      }));
    }
  };

  const handleTypeChange = (type: RaffleType) => {
    const typeConfig = getRaffleTypeConfig(type);
    setFormData(prev => ({
      ...prev,
      type,
      // Ajustar total_spots baseado no tipo selecionado
      total_spots: Math.min(prev.total_spots, typeConfig.maxNumbers)
    }));
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        return formData.title.length > 0 && formData.description.length > 0;
      case 2:
        return formData.type !== null;
      case 3:
        return formData.total_spots > 0 && formData.price_per_spot > 0;
      case 4:
        return formData.prize_description.length > 0 && formData.draw_date.length > 0;
      case 5:
        return formData.pix_key.length > 0 && formData.pix_beneficiary.length > 0;
      default:
        return true;
    }
  };

  const handleNextStep = () => {
    if (validateStep(currentStep)) {
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (!validateStep(5)) {
      return;
    }

    setLoading(true);
    try {
      let prizeImageUrl = null;

      // Upload prize image if provided
      if (formData.prize_image) {
        const fileExt = formData.prize_image.name.split('.').pop();
        const fileName = `${user.id}/${Date.now()}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('raffle-images')
          .upload(fileName, formData.prize_image);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('raffle-images')
          .getPublicUrl(fileName);

        prizeImageUrl = publicUrl;
      }

      if (isEditMode && editRaffleId) {
        // Update existing raffle
        const updateData: any = {
          title: formData.title,
          description: formData.description,
          type: formData.type,
          total_spots: formData.total_spots,
          price_per_spot: formData.price_per_spot,
          prize_description: formData.prize_description,
          draw_date: formData.draw_date,
          pix_key: formData.pix_key,
          pix_beneficiary: formData.pix_beneficiary,
          updated_at: new Date().toISOString()
        };

        // Only update prize_image_url if a new image was uploaded
        if (prizeImageUrl) {
          updateData.prize_image_url = prizeImageUrl;
        }

        const { data, error } = await supabase
          .from('raffles')
          .update(updateData)
          .eq('id', editRaffleId)
          .eq('creator_id', user.id)
          .select()
          .single();

        if (error) throw error;

        setCreatedRaffleId(data.id);
        setShowSuccessDialog(true);
      } else {
        // Create new raffle
        const { data, error } = await supabase
          .from('raffles')
          .insert({
            creator_id: user.id,
            title: formData.title,
            description: formData.description,
            type: formData.type,
            total_spots: formData.total_spots,
            price_per_spot: formData.price_per_spot,
            prize_description: formData.prize_description,
            prize_image_url: prizeImageUrl,
            draw_date: formData.draw_date,
            pix_key: formData.pix_key,
            pix_beneficiary: formData.pix_beneficiary,
            status: 'draft'
          })
          .select()
          .single();

        if (error) throw error;

        setCreatedRaffleId(data.id);
        setShowSuccessDialog(true);
      }
    } catch (error) {
      console.error('Error creating raffle:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSuccessDialog = () => {
    setShowSuccessDialog(false);
    if (createdRaffleId) {
      navigate('/creator-dashboard');
    }
  };

  const handleViewRaffle = () => {
    setShowSuccessDialog(false);
    if (createdRaffleId) {
      navigate(`/raffle/${createdRaffleId}/preview`);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <motion.div 
            className="space-y-6"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="text-center mb-6">
              <h2 className="text-2xl font-semibold text-foreground mb-2">
                Informações Básicas
              </h2>
              <p className="text-muted-foreground">
                Vamos começar com as informações principais da sua rifa
              </p>
            </div>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Título da Rifa *</Label>
                <Input
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Ex: Rifa do iPhone 15 Pro"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Descrição *</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Descreva sua rifa, regras e informações importantes..."
                  rows={5}
                  required
                />
              </div>
            </div>
          </motion.div>
        );

      case 2:
        const popularTypes = raffleTypes.filter(type => type.isPopular);
        const otherTypes = raffleTypes.filter(type => !type.isPopular);
        const selectedConfig = getRaffleTypeConfig(formData.type);
        
        return (
          <motion.div 
            className="space-y-8"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="text-center mb-6">
              <h2 className="text-2xl font-semibold text-foreground mb-2">
                Tipo de Rifa
              </h2>
              <p className="text-muted-foreground">
                Escolha o formato da sua rifa entre as opções mais populares ou explore outras categorias
              </p>
            </div>
            
            {/* Tipos Populares */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <span className="text-yellow-500">⭐</span>
                Mais Populares
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {popularTypes.map((type) => (
                  <ThemedCard 
                    key={type.id}
                    className={`p-4 cursor-pointer transition-all relative ${
                      formData.type === type.id
                        ? 'ring-2 ring-primary border-primary bg-primary/5 shadow-lg scale-105' 
                        : 'hover:border-primary/50 hover:shadow-md hover:scale-102'
                    }`}
                    onClick={() => handleTypeChange(type.id)}
                  >
                    <div className="text-center">
                      <div className="text-2xl mb-2">{type.icon}</div>
                      <h3 className="font-semibold text-foreground mb-1">{type.title}</h3>
                      <p className="text-xs text-muted-foreground mb-2">
                        {type.description}
                      </p>
                      <p className="text-xs text-primary font-medium">
                        Até {type.maxNumbers} opções
                      </p>
                    </div>
                    {formData.type === type.id && (
                      <div className="absolute -top-1 -right-1 h-6 w-6 bg-primary rounded-full flex items-center justify-center">
                        <CheckIcon className="h-4 w-4 text-white" />
                      </div>
                    )}
                  </ThemedCard>
                ))}
              </div>
            </div>

            {/* Outras Opções */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground">
                Outras Opções
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {otherTypes.map((type) => (
                  <ThemedCard 
                    key={type.id}
                    className={`p-3 cursor-pointer transition-all relative text-center ${
                      formData.type === type.id
                        ? 'ring-2 ring-primary border-primary bg-primary/5 shadow-lg scale-105' 
                        : 'hover:border-primary/50 hover:shadow-md hover:scale-102'
                    }`}
                    onClick={() => handleTypeChange(type.id)}
                  >
                    <div className="text-lg mb-1">{type.icon}</div>
                    <h4 className="text-xs font-medium text-foreground mb-1">{type.title}</h4>
                    <p className="text-xs text-primary">
                      {type.maxNumbers} opções
                    </p>
                    {formData.type === type.id && (
                      <div className="absolute -top-1 -right-1 h-5 w-5 bg-primary rounded-full flex items-center justify-center">
                        <CheckIcon className="h-3 w-3 text-white" />
                      </div>
                    )}
                  </ThemedCard>
                ))}
              </div>
            </div>

            {/* Preview da Seleção */}
            {formData.type && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <ThemedCard className="p-6 bg-gradient-to-br from-primary/10 to-purple-500/10 border-primary/20">
                  <div className="flex items-start gap-4">
                    <div className="text-3xl">{selectedConfig.icon}</div>
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-foreground mb-2">
                        {selectedConfig.title} Selecionado
                      </h4>
                      <p className="text-sm text-muted-foreground mb-3">
                        {selectedConfig.description}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded">
                          Máximo: {selectedConfig.maxNumbers} opções
                        </span>
                        {selectedConfig.isPopular && (
                          <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">
                            ⭐ Popular
                          </span>
                        )}
                      </div>
                      {selectedConfig.examples && (
                        <div className="mt-3">
                          <p className="text-xs text-muted-foreground mb-1">Exemplos:</p>
                          <div className="flex flex-wrap gap-1">
                            {selectedConfig.examples.slice(0, 5).map((example, index) => (
                              <span key={index} className="text-xs bg-muted px-2 py-1 rounded">
                                {example}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </ThemedCard>
              </motion.div>
            )}
          </motion.div>
        );

      case 3:
        return (
          <motion.div 
            className="space-y-6"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="text-center mb-6">
              <h2 className="text-2xl font-semibold text-foreground mb-2">
                Configuração de Valores
              </h2>
              <p className="text-muted-foreground">
                Defina a quantidade de cotas e o valor de cada uma
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="total_spots">Quantidade de Cotas *</Label>
                <Input
                  type="number"
                  id="total_spots"
                  name="total_spots"
                  value={formData.total_spots}
                  onChange={handleInputChange}
                  min="10"
                  max={getRaffleTypeConfig(formData.type).maxNumbers}
                  placeholder="100"
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Número total de cotas disponíveis (máximo: {getRaffleTypeConfig(formData.type).maxNumbers} para {getRaffleTypeLabel(formData.type)})
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="price_per_spot">Valor por Cota (R$) *</Label>
                <Input
                  type="number"
                  id="price_per_spot"
                  name="price_per_spot"
                  value={formData.price_per_spot}
                  onChange={handleInputChange}
                  min="0.5"
                  step="0.5"
                  placeholder="5.00"
                  required
                />
                <p className="text-xs text-muted-foreground">Valor que cada participante pagará por cota</p>
              </div>
            </div>
            
            <ThemedCard className="p-6 bg-gradient-to-br from-green-500/10 to-emerald-600/10 border-green-500/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-600 text-sm font-medium mb-1">Valor Total da Rifa</p>
                  <p className="text-3xl font-bold text-foreground">R$ {(formData.total_spots * formData.price_per_spot).toFixed(2)}</p>
                  <p className="text-xs text-muted-foreground mt-1">{formData.total_spots} cotas × R$ {formData.price_per_spot.toFixed(2)}</p>
                </div>
                <div className="h-12 w-12 bg-green-500/20 rounded-full flex items-center justify-center">
                  <CreditCardIcon className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </ThemedCard>
          </motion.div>
        );

      case 4:
        return (
          <motion.div 
            className="space-y-6"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="text-center mb-6">
              <h2 className="text-2xl font-semibold text-foreground mb-2">
                Prêmio e Data do Sorteio
              </h2>
              <p className="text-muted-foreground">
                Configure o prêmio e quando será realizado o sorteio
              </p>
            </div>
            
            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="prize_description">Descrição do Prêmio *</Label>
                <Textarea
                  id="prize_description"
                  name="prize_description"
                  value={formData.prize_description}
                  onChange={handleInputChange}
                  placeholder="Descreva detalhadamente o prêmio..."
                  rows={4}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="prize_image">Imagem do Prêmio</Label>
                <Input
                  type="file"
                  id="prize_image"
                  accept="image/*"
                  onChange={handleFileChange}
                />
                <p className="text-xs text-muted-foreground">Adicione uma foto do prêmio (opcional)</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="draw_date">Data do Sorteio *</Label>
                <Input
                  type="datetime-local"
                  id="draw_date"
                  name="draw_date"
                  value={formData.draw_date}
                  onChange={handleInputChange}
                  min={new Date().toISOString().slice(0, 16)}
                  required
                />
              </div>
            </div>
            
            <Alert>
              <CalendarIcon className="h-4 w-4" />
              <AlertDescription>
                O sorteio será realizado automaticamente na data e horário escolhidos. 
                Certifique-se de definir uma data futura adequada para a venda das cotas.
              </AlertDescription>
            </Alert>
          </motion.div>
        );

      case 5:
        return (
          <motion.div 
            className="space-y-6"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="text-center mb-6">
              <h2 className="text-2xl font-semibold text-foreground mb-2">
                Configuração de Pagamento PIX
              </h2>
              <p className="text-muted-foreground">
                Configure sua chave PIX para receber os pagamentos
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="pix_key">Chave PIX *</Label>
                <Input
                  type="text"
                  id="pix_key"
                  name="pix_key"
                  value={formData.pix_key}
                  onChange={handleInputChange}
                  placeholder="CPF, Email, Telefone ou Chave Aleatória"
                  required
                />
                <p className="text-xs text-muted-foreground">Chave PIX para receber os pagamentos</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="pix_beneficiary">Nome do Beneficiário *</Label>
                <Input
                  type="text"
                  id="pix_beneficiary"
                  name="pix_beneficiary"
                  value={formData.pix_beneficiary}
                  onChange={handleInputChange}
                  placeholder="Nome completo ou razão social"
                  required
                />
                <p className="text-xs text-muted-foreground">Nome que aparecerá no comprovante PIX</p>
              </div>
            </div>
            
            <ThemedCard className="p-6 bg-gradient-to-br from-blue-500/10 to-indigo-600/10 border-blue-500/20">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <CheckIcon className="h-5 w-5 text-blue-600" />
                Resumo da Rifa
              </h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div className="space-y-2">
                  <p><span className="text-muted-foreground">Título:</span> <span className="font-medium text-foreground">{formData.title}</span></p>
                  <p><span className="text-muted-foreground">Tipo:</span> <span className="font-medium text-foreground">{getRaffleTypeLabel(formData.type)}</span></p>
                  <p><span className="text-muted-foreground">Cotas:</span> <span className="font-medium text-foreground">{formData.total_spots}</span></p>
                </div>
                <div className="space-y-2">
                  <p><span className="text-muted-foreground">Valor por Cota:</span> <span className="font-medium text-foreground">R$ {formData.price_per_spot.toFixed(2)}</span></p>
                  <p><span className="text-muted-foreground">Valor Total:</span> <span className="font-bold text-blue-600">R$ {(formData.total_spots * formData.price_per_spot).toFixed(2)}</span></p>
                  <p><span className="text-muted-foreground">Data do Sorteio:</span> <span className="font-medium text-foreground">{formData.draw_date ? new Date(formData.draw_date).toLocaleString('pt-BR') : 'Não definida'}</span></p>
                </div>
              </div>
              <Alert className="mt-4">
                <AlertTriangleIcon className="h-4 w-4" />
                <AlertDescription>
                  <strong>Importante:</strong> Sua rifa será criada como <strong>rascunho</strong>. 
                  Para ativá-la e torná-la pública, você precisará pagar uma taxa de <strong>R$ 10,00</strong> 
                  que será debitada do seu saldo de créditos no painel do criador.
                </AlertDescription>
              </Alert>
            </ThemedCard>
          </motion.div>
        );

      default:
        return null;
    }
  };

  if (!user) {
    return (
      <BaseLayout variant="centered">
        <div className="min-h-[50vh] flex items-center justify-center">
          <ThemedCard className="p-8 text-center">
            <AlertTriangleIcon className="mx-auto h-12 w-12 text-amber-500 mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">
              Login Necessário
            </h2>
            <p className="text-muted-foreground mb-4">
              Você precisa estar logado para criar uma rifa.
            </p>
            <Button onClick={() => navigate('/auth/login')}>
              Fazer Login
            </Button>
          </ThemedCard>
        </div>
      </BaseLayout>
    );
  }

  return (
    <BaseLayout variant="wide">
      <div className="space-y-8">
        {/* Welcome Header */}
        <motion.div 
          className="text-center py-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-bold text-foreground mb-4">
            {isEditMode ? 'Editar Rifa 📝' : 'Criar Nova Rifa 🎯'}
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Configure sua rifa seguindo os passos abaixo e comece a vender cotas em poucos minutos.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="max-w-4xl mx-auto"
        >
          <ThemedCard className="p-8">
            {/* Progress Header */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <span className="text-lg font-semibold text-foreground">
                  Passo {currentStep} de {totalSteps}
                </span>
                <span className="text-sm font-medium text-primary bg-primary/10 px-3 py-1 rounded-full">
                  {Math.round((currentStep / totalSteps) * 100)}% concluído
                </span>
              </div>
              <Progress value={(currentStep / totalSteps) * 100} className="h-3" />
            </div>

            {/* Step Content */}
            <div className="min-h-[500px] mb-8">
              {renderStepContent()}
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-between pt-6 border-t">
              <Button
                variant="outline"
                onClick={handlePreviousStep}
                disabled={currentStep === 1}
                size="lg"
              >
                Anterior
              </Button>
              
              {currentStep < totalSteps ? (
                <Button
                  onClick={handleNextStep}
                  disabled={!validateStep(currentStep)}
                  size="lg"
                  className="bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-600/90"
                >
                  Próximo
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={loading || !validateStep(5)}
                  size="lg"
                  className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                >
                  {loading ? (isEditMode ? 'Salvando...' : 'Criando...') : (isEditMode ? '💾 Salvar Alterações' : '🚀 Criar Rifa')}
                </Button>
              )}
            </div>
          </ThemedCard>
        </motion.div>

        {/* Success Dialog */}
        <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-green-600">
                <CheckIcon className="h-5 w-5" />
                {isEditMode ? 'Rifa Atualizada com Sucesso!' : 'Rifa Criada com Sucesso!'}
              </DialogTitle>
              <DialogDescription>
                {isEditMode ? 'Suas alterações foram salvas com sucesso.' : 'Sua rifa foi criada como rascunho e está pronta para configuração final.'}
              </DialogDescription>
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mt-4">
                <div className="flex items-start gap-3">
                  <AlertTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div className="space-y-2">
                    <p className="font-semibold text-yellow-800 dark:text-yellow-200">
                      Próximos Passos:
                    </p>
                    <ul className="text-sm text-yellow-700 dark:text-yellow-300 space-y-1 list-disc list-inside">
                      <li>Vá para o Painel do Criador</li>
                      <li>Ative sua rifa pagando R$ 10,00</li>
                      <li>Após a ativação, ela ficará pública</li>
                      <li>Gerencie participações e pagamentos</li>
                    </ul>
                  </div>
                </div>
              </div>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={handleViewRaffle}>
                Ver Preview da Rifa
              </Button>
              <Button onClick={handleCloseSuccessDialog} className="bg-green-600 hover:bg-green-700">
                Ir para Painel do Criador
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </BaseLayout>
  );
};

export default CreateRafflePage;